import React, { memo, useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router';
import type { Playlist, PlaylistItem, BroadcastLivePlayItem } from '@jwp/ott-common/types/playlist';
import { formatDurationTag, formatSeriesMetaString } from '@jwp/ott-common/src/utils/formatting';
import { formatBroadcastCardSchedule } from '@jwp/ott-common/src/utils/broadcastLive';
import { isLiveChannel, isSeries } from '@jwp/ott-common/src/utils/media';
import { MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';
import Lock from '@jwp/ott-theme/assets/icons/lock.svg?react';
import Today from '@jwp/ott-theme/assets/icons/today.svg?react';
import MoreIcon from '@jwp/ott-theme/assets/icons/more.svg?react';
import InfoIcon from '@jwp/ott-theme/assets/icons/info.svg?react';
import CancelIcon from '@jwp/ott-theme/assets/icons/cancel.svg?react';
import PlayIcon from '@jwp/ott-theme/assets/icons/play.svg?react';
import { testId } from '@jwp/ott-common/src/utils/common';
import type { PosterAspectRatio } from '@jwp/ott-common/src/utils/collection';
import type { Subscription } from '@jwp/ott-common/types/subscription';
import { useAppInfo } from '@jwp/ott-hooks-react/src/useAppInfo';
import { isAfter } from 'date-fns';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

import { useAccountStore } from '../../../../common/src/stores/AccountStore';
import { useTheme } from '../../../../../ThemeContext';
import Image from '../Image/Image';
import Icon from '../Icon/Icon';
import { formatTime } from '../../utils/formatTime';

import { getSvgByIndex } from './TopTenIconRender';
import styles from './Card.module.scss';

type ReplaceColon<T> = T extends `${infer Left}:${infer Right}` ? `${Left}${Right}` : T;
type PosterAspectRatioClass = ReplaceColon<PosterAspectRatio>;
type MediaPreview = {
  src: string;
  width: number;
  type: string;
};

type CardProps = {
  item: PlaylistItem | BroadcastLivePlayItem;
  onHover?: () => void;
  progress?: number;
  posterAspect?: PosterAspectRatio;
  featured?: boolean;
  disabled?: boolean;
  loading?: boolean;
  isCurrent?: boolean;
  isLocked?: boolean;
  currentLabel?: string;
  url: string;
  headingLevel?: number;
  tabIndex?: number;
  isShowTopTenIcon?: boolean;
  topTenIndex?: number;
  railType?: string;
  isOverlayTestVisible?: boolean;
  isRecommendedVideo?: boolean;
  RecommendedVideoOnClick?: () => void;
  shelfTitle?: string;
  status?: 'APPROVED' | 'PENDING' | 'REJECTED' | 'scheduled' | 'live';
  onMenuClick?: (e: React.MouseEvent) => void;
  onClick?: (e: React.MouseEvent) => void;
  isReels?: boolean;
  isBordered?: boolean;
  playlist?: Playlist;
};

const jwImageCache = new Map<string, string>();
const pendingFetches = new Set<string>();

function Card({
  onHover,
  progress,
  item,
  posterAspect = '16:9',
  featured = false,
  disabled = false,
  loading = false,
  isCurrent = false,
  isLocked = true,
  currentLabel,
  headingLevel = 3,
  url,
  tabIndex = 0,
  isShowTopTenIcon,
  topTenIndex,
  railType,
  isOverlayTestVisible,
  isRecommendedVideo,
  RecommendedVideoOnClick,
  status = 'APPROVED',
  onMenuClick,
  onClick,
  isReels,
  isBordered = false,
  playlist,
}: CardProps): JSX.Element {
  const { title, duration, episodeNumber, seasonNumber, cardImage: image, mediaStatus, scheduledStart } = item;
  const { universalStyles } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const {
    t,
    i18n: { language },
  } = useTranslation(['common', 'video']);
  // t('play_item')
  const { heroBannerSize, isGoogleAnalytics, isAnalyticsEnable, isSubscriptionEnabled } = useAppConfig();
  const { config } = useConfigStore(({ config }) => ({ config }), shallow);
  const location = useLocation();
  const navigate = useNavigate();

  const { packageKey } = useAppInfo();
  const { subscription, allSubscriptions } = useAccountStore.getState();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [portraitImg, setPortraitImg] = useState<string>();
  const verticalImageAspectRatio = useMemo(() => ['2:3', '9:13', '9:16'], []);
  const isTopTen = railType === 'top-rated';
  const cardClassName = classNames(styles.card, {
    [styles.featured]: featured,
    [styles.disabled]: disabled && heroBannerSize !== 'small',
    [styles.topTenCard]: isTopTen,
    [styles.reelsCard]: isReels,
  });
  const aspectRatioClass = posterAspect ? styles[`aspect${posterAspect.replace(':', '') as PosterAspectRatioClass}`] : undefined;
  const posterClassNames = classNames(styles.poster, aspectRatioClass, {
    [styles.current]: isCurrent,
  });
  const posterImageClassNames = classNames(styles.posterImage, {
    [styles.visible]: imageLoaded,
    [styles.statusBgOverlay]: status === 'PENDING' || status === 'REJECTED',
  });

  const isSeriesItem = isSeries(item);
  const isLive = mediaStatus === MediaStatus.LIVE || isLiveChannel(item);
  const isScheduled = mediaStatus === MediaStatus.SCHEDULED;
  const isFree = item?.free || false;
  const { click } = useContentStateStore.getState();
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();

  const renderTag = () => {
    if (loading || !title) { return null; }

    if (isSeriesItem) {
      return <div className={styles.tag}>{t('video:series')}</div>;
    }

    if (isLive) {
      return (
        <div className={classNames(styles.tag, styles.live)} style={universalStyles.live}>
          {t('live')}
        </div>
      );
    }

    if (isScheduled && scheduledStart) {
      return <div className={styles.tag}>{formatBroadcastCardSchedule(scheduledStart, language)}</div>;
    }

    if (isScheduled) {
      return (
        <div className={styles.tag}>
          <Icon icon={Today} className={styles.scheduled} />
          {t('scheduled')}
        </div>
      );
    }

    if (episodeNumber) {
      return <div className={styles.tag}>{formatSeriesMetaString(seasonNumber, episodeNumber)}</div>;
    }

    if (duration) {
      return (
        <div className={classNames(styles.tag, { [styles.reelsTag]: isReels })}>
          {isReels && <Icon icon={PlayIcon} className={styles.playIcon} />}
          {isReels ? formatTime(duration) : formatDurationTag(duration)}
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    const id = item?.mediaid;

    if (isReels || (item as any).isReels) {
      setPortraitImg(item.cardImage || item.image);
      return;
    }

    if (!id) { return; }

    if (jwImageCache.has(id)) {
      setPortraitImg(jwImageCache.get(id));
      return;
    }

    if (pendingFetches.has(id)) { return; }

    const controller = new AbortController();

    const fetchPortraitImage = async () => {
      pendingFetches.add(id);
      try {
        const response = await fetch(`https://cdn.jwplayer.com/v2/media/${id}/images.json`, { signal: controller.signal });

        if (!response.ok) { throw new Error('JW fetch failed'); }

        const data = await response.json();

        const imageUrl =
          data?.vertical?.image_url ||
          data?.Vertical?.image_url ||
          data?.background?.image_url ||
          data?.Background?.image_url ||
          `https://cdn.jwplayer.com/thumbs/${id}.jpg`;

        jwImageCache.set(id, imageUrl);
        setPortraitImg(imageUrl);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') { return; }
        const fallback = `https://cdn.jwplayer.com/thumbs/${id}.jpg`;
        jwImageCache.set(id, fallback);
        setPortraitImg(fallback);
      } finally {
        pendingFetches.delete(id);
      }
    };

    if (railType || verticalImageAspectRatio.includes(posterAspect)) {
      fetchPortraitImage();
    }

    return () => controller.abort();
  }, [item?.mediaid, railType, posterAspect, verticalImageAspectRatio, isReels, (item as any).isReels]);

  // useEffect(() => {
  //   if (railType) {
  //     setPortraitImg(`https://img.jwplayer.com/v1/media/${item.mediaid}/images/vertical.jpg`);
  //   } else if (verticalImageAspectRatio.includes(posterAspect)) {
  //     setPortraitImg(`https://img.jwplayer.com/v1/media/${item.mediaid}/images/vertical.jpg`);
  //   }
  // }, [railType, item]);

  const handleError = () => {
    setPortraitImg(image);
  };

  const loadCardImage = () => {
    const videoPreview = item.images?.find((img) => img.type?.startsWith('video/'));
    const videoPreviews = item.images?.filter((img) => img.type?.startsWith('video/')) || [];

    const selectedVideo = featured
      ? videoPreviews.reduce<MediaPreview | undefined>((max, curr) => (curr.width > (max?.width || 0) ? curr : max), undefined)
      : videoPreviews.find((img) => img.width === 320);

    if (!disabled && isHovered && videoPreview) {
      return (
        <video
          className={posterImageClassNames}
          src={selectedVideo?.src}
          width={featured ? 1920 : 320}
          muted
          playsInline
          autoPlay
          loop
          onLoadedData={() => setImageLoaded(true)}
          style={{ objectFit: 'fill' }}
        />
      );
    }
    return (
      <>
        {railType || verticalImageAspectRatio.includes(posterAspect) ? (
          <Image
            className={posterImageClassNames}
            image={portraitImg}
            width={featured ? 1920 : 320}
            onLoad={() => setImageLoaded(true)}
            alt=""
            handleError={handleError}
            portraitImg={Boolean(railType) || verticalImageAspectRatio.includes(posterAspect)}
          />
        ) : (
          <Image className={posterImageClassNames} image={image} width={featured ? 1920 : 320} onLoad={() => setImageLoaded(true)} alt="" />
        )}
      </>
    );
  };

  const renderCardLock = () => {
    const isContentFree = isFree == 'true';
    if (packageKey && typeof item[packageKey] === 'undefined') { return false; }
    if (!allSubscriptions && packageKey && typeof item[packageKey]) {
      return true;
    }
    const isValid = (subcribed: Subscription) => {
      if (isContentFree) { return false; }
      if (packageKey && item[packageKey]?.split(',').includes(subcribed?.offerId) && subcribed?.status === 'active') { return false; }

      if (subcribed?.status === 'expired' || subcribed?.status === 'cancelled') {
        const expiryDate = new Date(subcribed?.expiresAt * 1000);
        const today = new Date();
        if (isAfter(expiryDate, today)) { return false; }
      }

      return true;
    };

    if (Array.isArray(allSubscriptions)) {
      return !allSubscriptions.some((sub) => !isValid(sub));
      // return subscription.some(isValid);
    }

    return allSubscriptions && isValid(allSubscriptions);
    // return subscription && isValid(subscription);
  };

  const handleNavigation = (e: React.MouseEvent) => {
    if (heroBannerSize !== 'small' && disabled) {
      e.preventDefault();
      return;
    }

    if (onClick) { onClick(e); }

    if (isAnalyticsEnable && !e.defaultPrevented) {
      handleCardClick();
    }

    if (isReels) {
      e.preventDefault();
      navigate(url, {
        state: {
          playlistItems: playlist?.playlist || [],
          playlistMeta: playlist,
          item,
        },
      });
    }
  };

  const handleCardClick = () => {
    const pathname = location.pathname;
    const segments = pathname.split('/');
    const contentIdFromPath = segments[2];

    const activeItem = config?.menu?.find((item) => item.contentId === contentIdFromPath);

    let label: string | undefined;
    if (pathname === '/') {
      label = 'Home';
    } else if (click?.label) {
      label = click.label;
    } else if (activeItem?.label) {
      label = activeItem?.label;
    }

    if (isAnalyticsEnable && label) {
      sendGAEvent('screen_view', {
        firebase_screen_class: label,
        firebase_screen: title,
        device_type: deviceType,
        userid_no: userId,
      });
    }
  };

  const heading = React.createElement(`h${headingLevel}`, { className: classNames(styles.title, { [styles.loading]: loading }) }, loading ? '' : title);

  return (
    <Link
      role="button"
      to={isReels ? undefined : url}
      className={cardClassName}
      onClick={handleNavigation}
      onMouseEnter={onHover}
      tabIndex={disabled ? -1 : tabIndex}
      data-testid={testId(title)}
      style={{ position: 'relative' }}
    >
      {!featured && isOverlayTestVisible && (
        <div className={styles.titleContainer}>
          <div className={styles.infoWrapper}>
            {heading}
            {/* {!!scheduledStart && (
              <div className={classNames(styles.scheduledStart, { [styles.loading]: loading })}>{formatLocalizedDateTime(scheduledStart, language)}</div>
            )} */}
          </div>
          {!isBordered && onMenuClick && (
            <button
              className={styles.menuButton}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMenuClick(e);
              }}
              aria-label="More options"
            >
              <Icon icon={MoreIcon} />
            </button>
          )}
        </div>
      )}
      {isShowTopTenIcon && <div className={styles.svgicon}>{topTenIndex !== undefined ? getSvgByIndex(topTenIndex) : null}</div>}
      <div className={posterClassNames} onClick={isRecommendedVideo ? RecommendedVideoOnClick : undefined}>
        {loadCardImage()}
        {isReels && <div className={styles.reelsGradient} />}
        {!loading && (
          <div className={classNames(styles.meta, { [styles.metaLeft]: isReels })}>
            <div className={styles.heroTitleContainer}>
              {isOverlayTestVisible && featured
                ? React.createElement(
                  `h${headingLevel}`,
                  {
                    className:
                      heroBannerSize !== 'small'
                        ? classNames(styles.title, styles.heroTitleText, { [styles.loading]: loading })
                        : classNames(styles.smallTitle, styles.heroTitleText, { [styles.loading]: loading }),
                  },
                  loading ? '' : title,
                )
                : null}
            </div>

            <div className={styles.tags}>
              {renderCardLock() && !disabled && isSubscriptionEnabled && (
                <div className={classNames(styles.tag, styles.lock)} aria-label={t('card_lock')} role="img">
                  <Icon icon={Lock} />
                </div>
              )}
              {renderTag()}
            </div>
          </div>
        )}
        {isCurrent && <div className={styles.currentLabel}>{currentLabel}</div>}
        {progress ? (
          <div className={styles.progressContainer} style={universalStyles.neutral400}>
            <div className={styles.progressBar} style={{ width: `${Math.round(progress * 100)}%`, ...universalStyles.progress }} />
          </div>
        ) : null}
        {(status === 'PENDING' || status === 'REJECTED') && (
          <div className={styles.statusOverlay}>
            <div className={styles.statusContent}>
              <Icon icon={status === 'REJECTED' ? CancelIcon : InfoIcon} className={styles.statusIcon} />
              <span>{status === 'REJECTED' ? t('video:approval_rejected', { defaultValue: 'Approval Rejected' }) : t('video:approval_pending')}</span>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}

export default memo(Card);
